import { Suspense, useEffect, useState, useRef, useMemo } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { BarChartLineFill } from "react-bootstrap-icons";
import { Typeahead } from "react-bootstrap-typeahead";
import { Await, useRouteLoaderData } from "react-router-dom";
import { axiosInstance } from "../../auth/Auth";
import LoadingIndicator from "../LoadingIndicator";
import TrendOverTime from "../TrendOverTime";
import { Popover, Whisper } from "rsuite";
import { useResolvedModules } from "../HelperHooks";

export default function SongSearchRow({ item, virtualizer, virtualRow, setCatalogueSong, showItemId = false }) {
    return (
        <Row
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            style={{ transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)` }}
            className={`py-1 border-bottom position-absolute w-100 hoverable`}
            onClick={() => setCatalogueSong(item)}>
            <Col xs={3} xl className=" align-content-center d-none d-xl-block">
                {item.artist}
            </Col>
            <Col xs={3} xl className=" align-content-center d-none d-xl-block">
                {item.title}
            </Col>
            <Col xs={2} xl={2} className=" align-content-center text-center d-none d-xl-block">
                {item.release_year}
            </Col>
            <Col xs={12} className=" align-content-center d-xl-none">
                <Row className="gx-1">
                    <Col xs={"auto"}>
                        <span className="fw-bold fw-xl-normal">{item.artist}</span>
                    </Col>
                    <Col xs={"auto"}>
                        <span className="fw-bold fw-xl-normal">-</span>
                    </Col>
                    <Col xs={"auto"}>
                        <span className="fw-bold fw-xl-normal">{item.title}</span>
                    </Col>
                    {item.release_year && (
                        <Col xs={"auto"}>
                            <span className="fw-bold fw-xl-normal">({item.release_year})</span>
                        </Col>
                    )}
                </Row>
            </Col>
            <Col xs={"auto"} xl={2} className=" align-content-center text-center">
                <span className=" d-xl-none">Total Spins: </span>
                <span className=" fw-bold fw-xl-normal">{item.spins}</span>
            </Col>
            {showItemId && (
                <Col xs={"auto"} xl={2} className=" align-content-center text-center">
                    <span className=" d-xl-none">Item ID: </span>
                    <span className=" fw-bold fw-xl-normal">{item.item_id}</span>
                </Col>
            )}
            <Col xs={"auto"} xl={1} className=" align-content-center px-xl-0" onClick={e => e.stopPropagation()}>
                <TrendDropdown item={item} />
            </Col>
        </Row>
    );
}

function TrendDropdown({ item }) {
    const { radios, radiosModuleSettings } = useRouteLoaderData("root");

    const [showTrendStations, setShowTrendStations] = useState(false);
    const [availableTrendStations, setAvailableTrendStations] = useState(null);
    const [trendStation, setTrendStation] = useState(null);
    const whisperRef = useRef(null);

    const resolvedModules = useResolvedModules();
    const musicTestingEnabled = useMemo(() => resolvedModules?.includes(7), [resolvedModules]);

    useEffect(() => {
        async function getTrendStations() {
            const response = await axiosInstance.get(`/music_test/${item.item_id}/item_on_radios?mtt_only=false`);
            setAvailableTrendStations(response.data);
        }
        if (showTrendStations) getTrendStations();
    }, [item.item_id, showTrendStations]);

    const speaker = (
        <Popover className="p-0" arrow={false}>
            <Suspense>
                <Await resolve={radios}>
                    {resolvedRadios => (
                        <Await resolve={radiosModuleSettings}>
                            {resolvedModuleSettings => {
                                const validRadioIds = new Set(resolvedModuleSettings.data.filter(r => r.module_id === 8).map(r => r.radio_id));
                                const filteredRadios = resolvedRadios.data
                                    .filter(radio => validRadioIds.has(radio.radio_id))
                                    .map(radio => ({
                                        ...radio,
                                        disabled: !availableTrendStations?.includes(radio.radio_id) || radio.mtt_only,
                                    }));

                                return (
                                    <Typeahead
                                        id={`dropdown-stations-tot-${item.item_id}`}
                                        options={filteredRadios}
                                        labelKey="radio_name"
                                        flip
                                        renderMenu={
                                            !availableTrendStations
                                                ? () => (
                                                      <div className="py-2">
                                                          <LoadingIndicator showText={false} />
                                                      </div>
                                                  )
                                                : undefined
                                        }
                                        clearButton
                                        open={showTrendStations}
                                        onChange={val => {
                                            const selected = val?.length ? val[0] : null;
                                            setTrendStation(selected);

                                            if (selected && whisperRef.current) {
                                                whisperRef.current.close();
                                            }
                                        }}
                                    />
                                );
                            }}
                        </Await>
                    )}
                </Await>
            </Suspense>
        </Popover>
    );

    return (
        <>
            <Whisper
                ref={whisperRef}
                placement="autoHorizontalStart"
                trigger="click"
                controlId={`s-${item.item_id}`}
                speaker={speaker}
                onOpen={() => setShowTrendStations(true)}
                onClose={() => setShowTrendStations(false)}>
                <Button variant="ra-yellow" size="sm" className="d-flex align-items-center column-gap-1 py-0 py-xl-1 mx-auto" disabled={!musicTestingEnabled}>
                    <BarChartLineFill /> Trend
                </Button>
            </Whisper>
            <TrendOverTime
                show={!!trendStation}
                onHide={() => setTrendStation(null)}
                radioId={trendStation?.radio_id}
                itemId={item.item_id}
                dataType={trendStation?.stream ? 1 : 2}
                daypartId={1}
                monthsLimit={8}
            />
        </>
    );
}
